import { useMemo, useState } from "react";
import { Spinner, Stack, XStack } from "tamagui";
import type { GasCylinder, LastOrderGasItem } from "@screen/Home/pages/GasShop/products";
import { formatOrderableGasTitle } from "@utils/func/gasTitle";
import { floorIntToLabel, floorIntToPicker, parseFloorToInt, PRESET_USAGE_LOCATIONS, usageToPicker } from "../kioskTheme";
import { KioskBackBtn, KioskCTA, SheetShell } from "./KioskParts";
import { StepFloor, StepLift, StepQty, StepUse } from "./InstallSteps";
import { useInstallAddToCart } from "./useInstallAddToCart";
import { useCustomerDeliveries } from "./useCustomerDeliveries";

interface CurState {
  useSel: string | null;
  useOther: string;
  lift: boolean; // true = 有電梯
  floorSel: string;
  floorOther: string;
  qty: number;
  note: string;
}

interface Props {
  product: GasCylinder;
  lastItem: LastOrderGasItem | null; // 上次同品項設定（提供「跟上次一樣」捷徑）
  initialQty?: number; // 預帶數量（「上次訂購」無位置品項→指定位置時沿用上次數量）
  onClose: () => void;
  onAdded: () => void; // 成功加入後，由首頁播飛入動畫 + 更新橫條
}

/**
 * 安裝位精靈：使用位置 → 樓層 → 電梯 → 數量（1F 跳過電梯步）。
 * 步驟 UI 在 InstallSteps，加入購物車的後端邏輯在 useInstallAddToCart，本檔只管流程與狀態。
 */
const InstallSheet = ({ product, lastItem, initialQty, onClose, onAdded }: Props) => {
  const [qi, setQi] = useState(0);
  // 從「要幾桶」摘要點某列跳回修改 → 設 true；該步選完直接回到數量步，不續往下一步
  const [editReturn, setEditReturn] = useState(false);
  const [cur, setCur] = useState<CurState>({
    useSel: null,
    useOther: "",
    lift: true,
    floorSel: "1F",
    floorOther: "",
    qty: initialQty && initialQty > 0 ? initialQty : 1,
    note: "",
  });
  // 使用者過去用過、非預設的使用位置 → 併入選項（例如曾填「烤箱」，下次直接出現為選項）
  const savedDeliveries = useCustomerDeliveries();
  const customUsages = useMemo(() => {
    const preset = new Set<string>(PRESET_USAGE_LOCATIONS as readonly string[]);
    const seen = new Set<string>();
    const out: string[] = [];
    for (const d of savedDeliveries) {
      const u = (d.usage_location || "").trim();
      if (u && !preset.has(u) && !seen.has(u)) {
        seen.add(u);
        out.push(u);
      }
    }
    return out;
  }, [savedDeliveries]);

  const usageLocation = cur.useSel === "其他" ? cur.useOther.trim() : cur.useSel || "";
  const floorInt = useMemo(() => parseFloorToInt(cur.floorSel, cur.floorOther), [cur.floorSel, cur.floorOther]);
  const isGround = cur.floorSel === "1F"; // 1F → 跳過電梯步驟
  const productName = formatOrderableGasTitle(product.kilogram);

  const { submitting, addToCart } = useInstallAddToCart({
    product,
    usageLocation,
    floorInt,
    qty: cur.qty,
    note: cur.note,
    lift: cur.lift,
    onAdded,
    onClose,
  });

  const lastLabel = lastItem?.install_location
    ? `${lastItem.install_location.usage_location} · ${floorIntToLabel(lastItem.install_location.floor)} · ${
        lastItem.install_location.is_elevator ? "有電梯" : "無電梯"
      } · 數量 ${lastItem.qty}${lastItem.install_location.note ? " · " + lastItem.install_location.note : ""}`
    : "";

  const useSameAsLast = () => {
    const loc = lastItem?.install_location;
    if (!loc) return;
    const u = usageToPicker(loc.usage_location);
    const f = floorIntToPicker(loc.floor);
    setCur({
      useSel: u.useSel,
      useOther: u.useOther,
      lift: loc.is_elevator,
      floorSel: f.floorSel,
      floorOther: f.floorOther,
      qty: lastItem!.qty || 1,
      note: loc.note || "",
    });
    setQi(3);
  };

  // 前進：編輯模式（從摘要跳回）選完直接回數量步；否則照正常下一步
  const advance = (normalNext: number) => {
    if (editReturn) {
      setEditReturn(false);
      setQi(3);
    } else {
      setQi(normalNext);
    }
  };

  const pickUse = (val: string) => {
    setCur((c) => ({ ...c, useSel: val, useOther: val === "其他" ? c.useOther : "" }));
    if (val !== "其他") setTimeout(() => advance(1), 220); // → 樓層
  };
  const pickFloor = (val: string) => {
    const ground = val === "1F";
    setCur((c) => ({ ...c, floorSel: val, floorOther: val === "6F+" ? c.floorOther : "", lift: ground ? true : c.lift }));
    if (val !== "6F+") {
      // 1F 跳過電梯直接到數量；其餘到電梯步（用 picked 值判斷，避免 memo 尚未更新）
      setTimeout(() => {
        if (editReturn) {
          setEditReturn(false);
          setQi(3);
        } else {
          setQi(ground ? 3 : 2);
        }
      }, 220);
    }
  };
  const pickLift = (val: boolean) => {
    setCur((c) => ({ ...c, lift: val }));
    setTimeout(() => advance(3), 220); // → 數量
  };

  // 跳回某步修改（修改完回數量步）
  const jumpToEdit = (step: number) => {
    setEditReturn(true);
    setQi(step);
  };

  // 樓層步（6F+ 手填後按下一步）：1F 跳過電梯
  const advanceFloor = () => {
    if (editReturn) {
      setEditReturn(false);
      setQi(3);
    } else {
      setQi(isGround ? 3 : 2);
    }
  };
  // 上一步（考慮 1F 跳過電梯：數量步返回時 1F 回樓層、其餘回電梯）
  const prevStep = () => (qi === 3 ? (isGround ? 1 : 2) : qi - 1);

  // 下一步是否可按（樓層驗證移到 qi=1）
  const nextDisabled = (qi === 0 && !usageLocation) || (qi === 1 && (cur.floorSel === "6F+" ? floorInt === null : false));
  const next = () => {
    if (qi === 3) return addToCart();
    if (qi === 1) return advanceFloor();
    advance(qi + 1);
  };

  return (
    <SheetShell
      onClose={onClose}
      dotsTotal={isGround ? 3 : 4}
      dotsCurrent={isGround && qi === 3 ? 2 : qi}
      rightLabel={productName}
      footer={
        <XStack gap={11}>
          {qi > 0 && (
            <Stack flexBasis={110} flexGrow={0}>
              <KioskBackBtn label="上一步" onPress={() => setQi(prevStep())} />
            </Stack>
          )}
          <Stack flex={1}>
            <KioskCTA
              label={qi === 3 ? "加入購物車" : "下一步"}
              disabled={nextDisabled || submitting}
              onPress={next}
              right={submitting ? <Spinner size="small" color="#fff" /> : undefined}
            />
          </Stack>
        </XStack>
      }>
      {qi === 0 && (
        <StepUse
          lastItem={lastItem}
          lastLabel={lastLabel}
          customUsages={customUsages}
          useSel={cur.useSel}
          useOther={cur.useOther}
          onUseSame={useSameAsLast}
          onPickUse={pickUse}
          onChangeOther={(t) => setCur((c) => ({ ...c, useOther: t }))}
        />
      )}
      {qi === 1 && (
        <StepFloor
          floorSel={cur.floorSel}
          floorOther={cur.floorOther}
          onPickFloor={pickFloor}
          onChangeOther={(t) => setCur((c) => ({ ...c, floorOther: t }))}
        />
      )}
      {qi === 2 && <StepLift lift={cur.lift} onPick={pickLift} />}
      {qi === 3 && (
        <StepQty
          qty={cur.qty}
          note={cur.note}
          productName={productName}
          usageLocation={usageLocation}
          floorInt={floorInt}
          floorSel={cur.floorSel}
          lift={cur.lift}
          onDec={() => setCur((c) => ({ ...c, qty: Math.max(1, c.qty - 1) }))}
          onInc={() => setCur((c) => ({ ...c, qty: Math.min(20, c.qty + 1) }))}
          onChangeNote={(t) => setCur((c) => ({ ...c, note: t }))}
          onJump={jumpToEdit}
        />
      )}
    </SheetShell>
  );
};

export default InstallSheet;
